import { useEffect, useState } from "react";
import { fetchUserById } from "../../features/auth/store/userSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { useSelector } from "react-redux";
import { Currency } from "../../features/auth/store/types/currency-types";
import { fetchCurrencies } from "../../features/auth/store/currencySlice";
import { TransactionDetail } from "./types";
import { TransactionDetailsModal } from "./TransactionDetailModal";

type TransactionTableProps = {
  transactions: TransactionDetail[];
  loading?: boolean;
};

const formatDate = (timestamp?: string) => {
  if (!timestamp) return "N/A";
  const date = new Date(timestamp);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
};

const formatTime = (timestamp?: string) => {
  if (!timestamp) return "N/A";
  const date = new Date(timestamp);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatAmountWithSymbol = (
  amount: string | number | undefined,
  symbol: string,
) => {
  if (amount === undefined || amount === null || amount === "") return "-";

  const numericAmount =
    typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(numericAmount)) return "-";

  const formattedNumber = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numericAmount);

  if (!symbol) return formattedNumber;

  const normalized = symbol.toLowerCase();
  const spacer = /^[A-Za-z]+$/.test(symbol) ? " " : "";
  if (
    normalized === "coins" ||
    normalized === "point" ||
    normalized === "points"
  ) {
    return `${formattedNumber}${spacer}${symbol}`;
  }

  return `${symbol}${spacer}${formattedNumber}`;
};

export const Transable = ({
  transactions,
  loading = false,
}: TransactionTableProps) => {
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dispatch = useAppDispatch();
  const { selectedUser, loading: userLoading } = useAppSelector(
    (state) => state.users,
  );
  const currencies: Currency[] = useSelector(
    (state: any) => state.currencies?.items ?? [],
  );

  const getCurrencySymbol = (currencyId?: number) => {
    const match = currencies.find(
      (currency: any) => currency.id === currencyId,
    );
    return match?.symbol || "";
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (!currencies || currencies.length === 0) {
      dispatch(fetchCurrencies({ perPage: 200 }));
    }
  }, [dispatch, currencies]);

  useEffect(() => {
    if (!selectedTransaction) return;

    dispatch(fetchUserById(selectedTransaction.user_id));
  }, [selectedTransaction, dispatch]);

  const handleRowClick = (purchase: any) => {
    const normalizedTransaction = { ...purchase };

    const price = formatAmountWithSymbol(
      purchase.price,
      getCurrencySymbol(purchase.currency_id) || purchase.currency_symbol || "",
    );
    const amount = JSON.parse(purchase.metadata)?.amount;
    let formattedNumber = amount || "";
    if (amount) {
      formattedNumber = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amount);
    }
    const display =
      purchase.type_text === "AIRTIME" ? `${formattedNumber} Credits` : amount;

    if (!purchase.items_details || purchase.items_details.length === 0) {
      normalizedTransaction.items_details = [
        {
          name:
            purchase.description || purchase.type_text || "Transaction Item",
          username: "",
          image: "",
          currency_amount: display || "",
          price,
        },
      ];
    }

    normalizedTransaction.subtotal = purchase.subtotal || purchase.price;
    normalizedTransaction.fee = purchase.fee || "0.00";

    setSelectedTransaction(normalizedTransaction);
    setIsModalOpen(true);
  };

  const statusBgStyles: Record<string, string> = {
    PENDING: "bg-orange-100",
    SUCCESSFUL: "bg-green-100",
    PAID: "bg-green-100",
    REVERSED: "bg-blue-100",
    FAILED: "bg-red-100",
    QUEUED: "bg-yellow-200",
    PROCESSING: "bg-yellow-100",
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!transactions || transactions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 border border-[#FFFFFF1A] rounded-2xl">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">
            No transactions found
          </p>
          <p className="text-gray-400 dark:text-gray-500 text-sm">
            Try adjusting your search filters to see more results
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto">
        <table className="w-full border border-[#FFFFFF1A] rounded-2xl overflow-hidden">
          <thead>
            <tr className="bg-gray-100 dark:bg-[#FFFFFF1A] text-gray-600 dark:text-white uppercase text-xs bp:text-sm leading-normal h-[50px]">
              <th className="hidden px-6 text-left sm:table-cell">ID</th>
              <th className="px-6 text-left sm:hidden">STATUS</th>
              <th className="px-6 text-left">Type</th>
              <th className="px-6 text-left hidden sm:table-cell">
                Date & Time
              </th>
              <th className="px-6 text-left hidden sm:table-cell">Status</th>

              <th className="px-6 text-left">Amount</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 dark:text-white text-xs bp:text-sm">
            {transactions.map((transaction) => (
              <tr
                key={transaction.id}
                className="border-b border-gray-200 h-[50px] hover:bg-gray-50 dark:hover:bg-gray-50/5 cursor-pointer"
                onClick={() => handleRowClick(transaction)}
              >
                <td className="hidden sm:table-cell px-6">{transaction.id}</td>
                <td className="sm:hidden px-6">
                  <div className="flex items-center gap-3">
                    <div
                      className={`h-2.5 w-2.5 rounded-full shrink-0 ${
                        statusBgStyles[transaction.status_text] ||
                        "bg-orange-100"
                      }`}
                    />
                    <span>{transaction.id}</span>
                  </div>
                </td>
                <td className="px-6">
                  {transaction.type_text || transaction.type}
                </td>
                <td className="px-6 hidden sm:table-cell">
                  <div className="flex flex-col">
                    <span>{formatDate(transaction.created_at)}</span>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                      {formatTime(transaction.created_at)}
                    </span>
                  </div>
                </td>
                <td className="px-6 hidden sm:table-cell">
                  <StatusBadge
                    statusText={transaction.status_text || transaction.status}
                  />
                </td>

                <td className="px-6 font-bold">
                  {formatAmountWithSymbol(
                    transaction.price ?? transaction.amount,
                    getCurrencySymbol(transaction.currency_id) ||
                      transaction.currency_symbol ||
                      transaction.currency?.symbol ||
                      "",
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TransactionDetailsModal
        isOpen={isModalOpen}
        isLoading={userLoading}
        onClose={closeModal}
        transaction={selectedTransaction}
        getCurrencySymbol={getCurrencySymbol}
        formatAmountWithSymbol={formatAmountWithSymbol}
        formatDate={formatDate}
        formatTime={formatTime}
        selectedUser={selectedUser}
      />
    </div>
  );
};

const StatusBadge = ({ statusText }: { statusText?: string }) => {
  const statusStyles: Record<string, string> = {
    PENDING: "text-orange-500",
    SUCCESSFUL: "text-green-500",
    PAID: "text-green-500",
    REVERSED: "text-blue-500",
    FAILED: "text-red-500",
    PROCESSING: "text-yellow-500",
    QUEUED: "text-yellow-600",
  };

  return (
    <span
      className={`px-2 py-1 rounded text-sm font-medium ${
        statusText ? statusStyles[statusText] : "text-gray-500"
      }`}
    >
      {statusText || "Unknown"}
    </span>
  );
};
